import { BigNumber } from '@ethersproject/bignumber'
import { Trans } from '@lingui/macro'
import { CurrencyAmount } from '@uniswap/sdk-core'
import { useWeb3React } from '@web3-react/core'
import { TransactionConfirmationContext } from 'contexts/TransactionConfirmationContext'
import { useIncentivesRewardTokensQuery } from 'graphql/thegraph/incentives'
import { useCallback, useContext, useState } from 'react'
import styled from 'styled-components/macro'
import { transactionErrorToUserReadableMessage } from 'utils/transactionErrorToUserReadableMessage'

import { ButtonPrimary } from '../../components/Button'
import Card from '../../components/Card'
import { RowBetween } from '../../components/Row'
import { Skeleton } from '../../components/Skeleton'
import { useToken } from '../../hooks/Tokens'
import { useV3StakerContract } from '../../hooks/useContract'
import { useSingleCallResult } from '../../lib/hooks/multicall'
import { useTransactionAdder } from '../../state/transactions/hooks'
import { TransactionType } from '../../state/transactions/types'
import { ThemedText } from '../../theme/components/text'

const ClaimableRewardsCard = styled(Card)`
  background-color: ${({ theme }) => theme.backgroundSurface};
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
  padding: 1.25rem;
  border-radius: 16px;
  margin-top: 82px; /* Account for header height */

  @media screen and (max-width: 992px) {
    margin-top: 0; /* Remove margin on small screens */
  }
`

const RewardsTitle = styled.div`
  margin-bottom: 0.75rem;
`

const RewardsRow = styled(RowBetween)`
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 0.75rem;

  &:last-child {
    margin-bottom: 0;
  }
`

const RewardsAmount = styled.div`
  font-size: 20px;
  font-weight: 500;
  color: ${({ theme }) => theme.textPrimary};
  @media screen and (max-width: 992px) {
    font-size: 16px; /* Reduce font size on small screens */
  }
`

const ClaimButton = styled(ButtonPrimary)`
  width: 80px; /* Fixed size for the button */
  flex-shrink: 0;
  font-size: 16px;
  padding: 8px 16px;
  border-radius: 12px;
  text-align: center;
`

const NoRewardsMessage = styled(ThemedText.BodySmall)`
  text-align: center;
  color: ${({ theme }) => theme.textSecondary};
  margin: 1rem 0;
`

// Component that handles a single reward token row
function RewardRow({ rewardTokenAddress }: { rewardTokenAddress: string }) {
  const { account } = useWeb3React()
  const v3StakerContract = useV3StakerContract()
  const addTransaction = useTransactionAdder()
  const [claiming, setClaiming] = useState(false)
  const { setTransactionData } = useContext(TransactionConfirmationContext)

  // Get the token information
  const rewardToken = useToken(rewardTokenAddress)

  // Fetch earned rewards using useSingleCallResult
  const earnedRewardResult = useSingleCallResult(v3StakerContract, 'rewards', [rewardToken?.address, account])

  const earnedRewardBigNumber: BigNumber | undefined = earnedRewardResult?.result?.[0]

  // Convert to CurrencyAmount for formatting
  const earnedRewardAmount =
    earnedRewardBigNumber && rewardToken
      ? CurrencyAmount.fromRawAmount(rewardToken, earnedRewardBigNumber.toString())
      : undefined

  const hasRewards = earnedRewardAmount && earnedRewardAmount.greaterThan(0)

  const handleClaim = useCallback(async () => {
    if (!v3StakerContract || !account || !rewardToken || !hasRewards) return

    setTransactionData({
      showConfirm: true,
      attempting: true,
      pendingText: `Claiming ${rewardToken.symbol} rewards...`,
      hash: '',
      txError: '',
    })

    try {
      setClaiming(true)

      // Call the claimReward function on the staker contract
      const tx = await v3StakerContract.claimReward(
        rewardToken.address,
        account,
        0 // Amount to claim - 0 means claim all available rewards
      )

      setTransactionData((transactionData) => ({
        ...transactionData,
        showConfirm: true,
        attempting: false,
        hash: tx.hash,
      }))

      // Add transaction to state for user feedback
      addTransaction(tx, {
        type: TransactionType.CLAIM,
        recipient: account,
      })

      await tx.wait()
    } catch (error) {
      console.error('Error claiming rewards:', error)
      setTransactionData((transactionData) => ({
        ...transactionData,
        attempting: false,
        showConfirm: true,
        hash: '',
        txError: transactionErrorToUserReadableMessage(error, 'Unknown error occurred while claiming rewards'),
      }))
    } finally {
      setClaiming(false)
    }
  }, [v3StakerContract, account, rewardToken, hasRewards, addTransaction, setTransactionData])

  return (
    <RewardsRow>
      <RewardsAmount>
        {earnedRewardResult.loading || earnedRewardAmount === undefined ? (
          <Skeleton width={100} height={28} borderRadius={4} />
        ) : hasRewards ? (
          `${earnedRewardAmount.toSignificant(6)} ${rewardToken?.symbol}`
        ) : (
          `0 ${rewardToken?.symbol || ''}`
        )}
      </RewardsAmount>
      <ClaimButton onClick={handleClaim} disabled={!hasRewards || claiming || earnedRewardResult.loading}>
        {claiming ? <Trans>Claiming...</Trans> : <Trans>Claim</Trans>}
      </ClaimButton>
    </RewardsRow>
  )
}

export default function ClaimableRewards() {
  // Query to get all unique reward token addresses from all incentives
  const { data: rewardTokenAddresses, loading: loadingRewardTokens } = useIncentivesRewardTokensQuery()

  return (
    <ClaimableRewardsCard>
      <RewardsTitle>
        <ThemedText.SubHeaderSmall>
          <Trans>Your claimable rewards</Trans>
        </ThemedText.SubHeaderSmall>
      </RewardsTitle>

      {loadingRewardTokens ? (
        // Show skeleton loader while loading reward tokens
        <Skeleton width="100%" height={60} borderRadius={8} />
      ) : rewardTokenAddresses && rewardTokenAddresses.length > 0 ? (
        // Map through all reward token addresses and render a row for each
        rewardTokenAddresses.map((tokenAddress) => <RewardRow key={tokenAddress} rewardTokenAddress={tokenAddress} />)
      ) : (
        // Show a message if no reward tokens are found
        <NoRewardsMessage>
          <Trans>No claimable rewards found</Trans>
        </NoRewardsMessage>
      )}
    </ClaimableRewardsCard>
  )
}
